import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { listCourseProgress, toSortParam } from '../features/progress/progressApi';
import type { ProgressSortField, StudentProgress } from '../features/progress/progressApi';

interface ProgressTableProps {
  courseId: string;
  chapterId?: string;
  selectedStudentId?: string | null;
  onSelectStudent: (student: StudentProgress) => void;
}

const COLUMNS: { field: ProgressSortField; label: string }[] = [
  { field: 'name', label: 'Nom' },
  { field: 'totalPoints', label: 'Points' },
  { field: 'completionPct', label: 'Progression' },
  { field: 'lastActivity', label: 'Dernière activité' },
];

function sortIndicator(
  field: ProgressSortField,
  activeField: ProgressSortField,
  descending: boolean,
): string {
  if (field !== activeField) {
    return '';
  }
  return descending ? ' ▼' : ' ▲';
}

function formatLastActivity(isoDate: string): string {
  const parsed = new Date(isoDate);
  return Number.isNaN(parsed.getTime()) ? isoDate : parsed.toLocaleString('fr-CA');
}

function ProgressTable({
  courseId,
  chapterId,
  selectedStudentId,
  onSelectStudent,
}: ProgressTableProps) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<StudentProgress[]>([]);
  const [sortField, setSortField] = useState<ProgressSortField>('name');
  const [sortDescending, setSortDescending] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;

    async function loadProgress() {
      try {
        setLoading(true);
        setRows([]);
        setErrorMessage(null);
        const sort = toSortParam(sortField, sortDescending);
        const filter = filterText.trim() || undefined;
        const result = chapterId
          ? await listCourseProgress(courseId, sort, filter, chapterId)
          : await listCourseProgress(courseId, sort, filter);
        if (!ignore) {
          setRows(result);
          setErrorMessage(null);
        }
      } catch (error) {
        if (!ignore) {
          setErrorMessage(
            error instanceof Error ? error.message : 'Impossible de charger la progression.',
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void loadProgress();
    return () => {
      ignore = true;
    };
  }, [courseId, chapterId, sortField, sortDescending, filterText]);

  function handleSort(field: ProgressSortField) {
    if (field === sortField) {
      setSortDescending((current) => !current);
    } else {
      setSortField(field);
      setSortDescending(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      {errorMessage && <p role="alert">{errorMessage}</p>}
      {loading && <p role="status">{t('instructor.progressLoading')}</p>}

      <label>
        Filtrer par nom
        <input
          type="text"
          value={filterText}
          onChange={(event) => setFilterText(event.target.value)}
          placeholder="Rechercher un étudiant"
        />
      </label>

      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th key={column.field}>
                  <button type="button" onClick={() => handleSort(column.field)}>
                    {column.label}
                    {sortIndicator(column.field, sortField, sortDescending)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.userId}>
                <td>
                  <button
                    type="button"
                    aria-pressed={row.userId === selectedStudentId}
                    onClick={() => onSelectStudent(row)}
                  >
                    {row.name}
                  </button>
                </td>
                <td>{row.totalPoints}</td>
                <td>{row.completionPct}%</td>
                <td>
                  {row.lastActivity
                    ? formatLastActivity(row.lastActivity)
                    : t('instructor.noChapterActivity')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && !errorMessage && !loading && (
        <p>
          {filterText.trim()
            ? t('instructor.noMatchingStudents')
            : 'Aucun étudiant inscrit pour le moment.'}
        </p>
      )}
    </div>
  );
}

export default ProgressTable;
